import { useHead } from '@unhead/react'
import type { ResolvableMeta } from 'unhead/types'

export type AppSeoConfig = {
  title: string
  description: string
  canonicalHref?: string
  meta?: ResolvableMeta[]
}

/**
 * Generic SEO hook for all pages in the app.
 * Centralizes useHead calls and metadata management.
 * Tip: Consumers can set `tagPriority` on each meta item passed via `meta`
 * (for example: `high`, `critical`, `low`, `before:meta:name:description`).
 * https://unhead.unjs.io/docs/head/guides/core-concepts/positions
 */
export const useAppSeo = ({ title, description, canonicalHref, meta = [] }: AppSeoConfig) => {
  const defaultDescriptionMeta = {
    name: 'description',
    content: description,
    tagPriority: 'high',
  } as ResolvableMeta

  const hasCustomDescription = meta.some(
    entry =>
      typeof entry === 'object' && entry !== null && 'name' in entry && entry.name === 'description'
  )

  useHead({
    title,
    meta: [...(!hasCustomDescription ? [defaultDescriptionMeta] : []), ...meta],
    link: canonicalHref ? [{ rel: 'canonical', href: canonicalHref }] : undefined,
  })
}
